import React, { useState, useEffect } from 'react';
import './Desktop.css';
import { useScreenDimensions } from '../hooks/useScreenDimensions';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, set, push, update } from 'firebase/database';
import { db } from '../config/firebase';
import JSZip from 'jszip';
import MenuBar from './MenuBar';
import DesktopIcon from './DesktopIcon';
import Window from './Window';
import FileBrowser from './FileBrowser';
import TextEditor from './TextEditor';
import MusicPlayer from './MusicPlayer';
import MacPaint from './MacPaint';
import PhotoBooth from './PhotoBooth';
import Garden from './Garden';
import LoginSignupWindow from './LoginSignupWindow';
import OrbitTV from './OrbitTV';
import Guestbook from './Guestbook';
import OrbitOS from './OrbitOS';
import AdhivWebsite from './AdhivWebsite';
import OrbitRun from './OrbitRun';
import Genius from './Genius';
import ZoomAnimation from './ZoomAnimation';
import ImagePreview from './ImagePreview';
import MP3Preview from './MP3Preview';
import VideoPreview from './VideoPreview';
import Terminal from './Terminal';
import TerminalPreview from './TerminalPreview';
import HtmlAppFrame from './HtmlAppFrame';
import CodeEditor from './CodeEditor';
import folderPurple from '../img/folder-purple.png';
import cdIcon from '../img/cd-icon.png';
import fileIcon from '../img/file.png';
import photoboothIcon from '../img/photobooth22.png';
import gardenIcon from '../img/garden.png';
import guestbookIcon from '../img/guestbook.png';
import trashIcon from '../img/trash.png';
import tvIcon from '../img/tv.png';
import adhivIcon from '../img/adhiv2.png';
import orbitRunIcon from '../img/orbitrun.png';
import geniusIcon from '../img/genius.png';
import terminalIcon from '../img/terminal.svg';

interface OpenWindow {
  id: string;
  title: string;
  component: React.ReactNode;
  position: { x: number; y: number };
  dimensions?: { width: number; height: number };
  resizable?: boolean;
  type?: string; // For dynamic rendering (e.g., 'music-player')
  modal?: boolean; // For windows that should appear on top (e.g., login/signup)
}

interface ZoomState {
  active: boolean;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  endWidth: number;
  endHeight: number;
  onComplete: () => void;
}

interface UserApp {
  id: string;        // Firebase ID (internal)
  appId: string;     // Human-readable slug (e.g., "pomodoro-timer")
  name: string;
  emoji: string;
  code: string;
  css: string;
  createdAt: number;
  updatedAt: number;
  isPublished: boolean;
  description: string;
  format?: string;   // 'jsx-v1' (default) | 'html-v2' (built externally via MCP)
}

// Generate a URL-safe slug from app name
const generateAppSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special chars and emojis
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/-+/g, '-')       // Remove duplicate hyphens
    .replace(/^-|-$/g, '')     // Trim leading/trailing hyphens
    || 'app';                  // Fallback if empty
};

interface DesktopProps {
  initialRoute: string;
}

const Desktop: React.FC<DesktopProps> = ({ initialRoute }) => {
  const [openWindows, setOpenWindows] = useState<OpenWindow[]>([]);
  const [focusedWindowId, setFocusedWindowId] = useState<string | null>(null);
  const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);
  const [zoomAnimation, setZoomAnimation] = useState<ZoomState | null>(null);
  const [paintInComplete, setPaintInComplete] = useState(false);
  
  // User apps state
  const [userApps, setUserApps] = useState<UserApp[]>([]);
  
  // Published apps (from all users). Loaded from the metadata index —
  // code/css/html are fetched lazily when an app is opened.
  const [publishedApps, setPublishedApps] = useState<{
    id: string;        // Firebase ID
    appId: string;     // Human-readable slug
    name: string;
    emoji: string;
    code: string;
    css: string;
    authorId: string;
    authorHandle: string;
    publishedAt: number;
    description: string;
    format?: string;
  }[]>([]);
  
  // Installed app IDs (references to published apps)
  const [installedAppIds, setInstalledAppIds] = useState<string[]>([]);
  
  // Auth context
  const { currentUser, userHandle } = useAuth();

  // Screen dimensions for responsive window sizing/positioning
  const screenDimensions = useScreenDimensions();

  // Global music state
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [showMusicPlayerTooltip, setShowMusicPlayerTooltip] = useState(false);
  const [hasDismissedMusicPlayerTooltip, setHasDismissedMusicPlayerTooltip] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const tracks = [
    { name: 'la paix', file: '/audio/la paix.mp3' },
    { name: 'your orbit', file: '/audio/your orbit.mp3' },
    { name: 'things change', file: '/audio/things change.mp3' },
    { name: 'everytime', file: '/audio/everytime.mp3' },
    { name: 'need ya', file: '/audio/need ya.mp3' },
    { name: 'simmering', file: '/audio/simmering.mp3' }
  ];

  // Load user apps from Firebase
  useEffect(() => {
    const loadUserApps = async () => {
      if (!currentUser) {
        setUserApps([]);
        return;
      }
      
      try {
        const appsRef = ref(db, `userApps/${currentUser.uid}`);
        const snapshot = await get(appsRef);
        
        if (snapshot.exists()) {
          const appsData = snapshot.val();
          const existingIds: string[] = [];
          const apps: UserApp[] = [];
          
          // First pass: collect existing appIds
          Object.entries(appsData).forEach(([, data]: [string, any]) => {
            if (data.appId) {
              existingIds.push(data.appId);
            }
          });
          
          // Second pass: process apps, generating appIds for those missing them
          for (const [id, data] of Object.entries(appsData) as [string, any][]) {
            let appId = data.appId;
            
            // Generate appId for legacy apps that don't have one
            if (!appId) {
              const baseSlug = generateAppSlug(data.name || 'app');
              let slug = baseSlug;
              let counter = 2;
              while (existingIds.includes(slug)) {
                slug = `${baseSlug}-${counter}`;
                counter++;
              }
              appId = slug;
              existingIds.push(appId);
              
              // Update Firebase with the new appId
              const appRef = ref(db, `userApps/${currentUser.uid}/${id}`);
              update(appRef, { appId }).catch(err => 
                console.error('Error updating app with appId:', err)
              );
            }
            
            apps.push({
              id,
              appId,
              ...data
            });
          }
          
          // Sort by createdAt descending (newest first)
          apps.sort((a, b) => b.createdAt - a.createdAt);
          setUserApps(apps);
        } else {
          setUserApps([]);
        }
      } catch (error) {
        console.error('Error loading user apps:', error);
      }
    };
    
    loadUserApps();
  }, [currentUser]);

  // Generate a unique app slug
  const generateUniqueSlug = (name: string, existingIds: string[]): string => {
    const baseSlug = generateAppSlug(name);
    let slug = baseSlug;
    let counter = 2;
    
    while (existingIds.includes(slug)) {
      slug = `${baseSlug}-${counter}`;
      counter++;
    }
    
    return slug;
  };

  // Save a new user app to Firebase
  const saveUserApp = async (app: Omit<UserApp, 'id' | 'appId' | 'createdAt' | 'updatedAt' | 'isPublished'>): Promise<string> => {
    if (!currentUser) throw new Error('Must be logged in');
    
    // Generate unique slug
    const existingIds = [
      ...userApps.map(a => a.appId),
      ...publishedApps.map(a => a.appId || a.id)
    ];
    const appSlug = generateUniqueSlug(app.name, existingIds);
    
    const appsRef = ref(db, `userApps/${currentUser.uid}`);
    const newAppRef = push(appsRef);
    
    const newApp: Omit<UserApp, 'id'> = {
      ...app,
      appId: appSlug,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      isPublished: false
    };
    
    await set(newAppRef, newApp);
    
    const firebaseId = newAppRef.key!;
    
    // Update local state
    setUserApps(prev => [{
      id: firebaseId,
      ...newApp
    }, ...prev]);
    
    return firebaseId;
  };

  // Update an existing user app
  const updateUserApp = async (appId: string, updates: Partial<UserApp>): Promise<void> => {
    if (!currentUser) throw new Error('Must be logged in');
    
    const appRef = ref(db, `userApps/${currentUser.uid}/${appId}`);
    await update(appRef, {
      ...updates,
      updatedAt: Date.now()
    });
    
    // Update local state
    setUserApps(prev => prev.map(app => 
      app.id === appId 
        ? { ...app, ...updates, updatedAt: Date.now() }
        : app
    ));
  };

  // Delete a user app
  const deleteUserApp = async (appId: string): Promise<void> => {
    if (!currentUser) throw new Error('Must be logged in');
    
    // First check if the app is published
    const app = userApps.find(a => a.id === appId);
    if (app?.isPublished) {
      // Remove from published apps + index first
      const publishedRef = ref(db, `publishedApps/${app.appId}`);
      await set(publishedRef, null);
      await set(ref(db, `publishedAppsIndex/${app.appId}`), null);
      setPublishedApps(prev => prev.filter(p => p.appId !== app.appId));
    }
    
    // Delete the app
    const appRef = ref(db, `userApps/${currentUser.uid}/${appId}`);
    await set(appRef, null);
    
    // Update local state
    setUserApps(prev => prev.filter(a => a.id !== appId));
  };

  // Load published apps metadata from Firebase (all users). Prefers the
  // lightweight publishedAppsIndex; falls back to the full node if the index
  // is missing (pre-index data).
  useEffect(() => {
    const loadPublishedApps = async () => {
      try {
        const indexSnapshot = await get(ref(db, 'publishedAppsIndex'));
        let appsData = indexSnapshot.exists() ? indexSnapshot.val() : null;

        if (!appsData) {
          const fullSnapshot = await get(ref(db, 'publishedApps'));
          appsData = fullSnapshot.exists() ? fullSnapshot.val() : null;
        }

        if (appsData) {
          const apps = Object.entries(appsData).map(([id, data]: [string, any]) => ({
            code: '',
            css: '',
            id,
            // For legacy apps, use the Firebase key as appId, or generate from name
            appId: data.appId || id,
            ...data
          }));
          // Sort by publishedAt descending (newest first)
          apps.sort((a: any, b: any) => b.publishedAt - a.publishedAt);
          setPublishedApps(apps);
        } else {
          setPublishedApps([]);
        }
      } catch (error) {
        console.error('Error loading published apps:', error);
      }
    };

    loadPublishedApps();
  }, []);

  // Fetch a published app's full payload (code/css or html) by slug
  const fetchPublishedApp = async (slug: string): Promise<any | null> => {
    try {
      const snapshot = await get(ref(db, `publishedApps/${slug}`));
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Error fetching published app:', error);
      return null;
    }
  };

  // Load installed app IDs for current user
  useEffect(() => {
    const loadInstalledApps = async () => {
      if (!currentUser) {
        setInstalledAppIds([]);
        return;
      }
      
      try {
        const installedRef = ref(db, `installedApps/${currentUser.uid}`);
        const snapshot = await get(installedRef);
        
        if (snapshot.exists()) {
          const installedData = snapshot.val();
          setInstalledAppIds(Object.keys(installedData));
        } else {
          setInstalledAppIds([]);
        }
      } catch (error) {
        console.error('Error loading installed apps:', error);
      }
    };
    
    loadInstalledApps();
  }, [currentUser]);

  // Publish an app to the store (uses appId slug as the key for direct links)
  const publishApp = async (firebaseId: string): Promise<void> => {
    if (!currentUser || !userHandle) throw new Error('Must be logged in');
    
    // Fetch fresh data from Firebase to ensure we publish the latest version
    const appRef = ref(db, `userApps/${currentUser.uid}/${firebaseId}`);
    const snapshot = await get(appRef);
    if (!snapshot.exists()) throw new Error('App not found');
    
    const appData = snapshot.val();
    const appId = appData.appId;
    if (!appId) throw new Error('App has no appId');

    const publishedAt = Date.now();
    const publishedRef = ref(db, `publishedApps/${appId}`);

    if (appData.format === 'html-v2') {
      // Externally-built app: the html payload only lives in publishedApps, so
      // never overwrite it from the (payload-less) userApps mirror — just sync metadata.
      const existing = await get(publishedRef);
      if (!existing.exists() || !existing.val().html) {
        throw new Error('This app was built externally — republish it from Claude Code (MCP) instead.');
      }
      await update(publishedRef, {
        name: appData.name,
        emoji: appData.emoji,
        description: appData.description || '',
        publishedAt
      });
    } else {
      // Use the app's slug (appId) as the key in publishedApps for direct links
      await set(publishedRef, {
        appId: appId,
        name: appData.name,
        emoji: appData.emoji,
        code: appData.code,
        css: appData.css,
        authorId: currentUser.uid,
        authorHandle: userHandle,
        publishedAt,
        description: appData.description || '',
        format: 'jsx-v1'
      });
    }

    // Keep the lightweight store index in sync
    await set(ref(db, `publishedAppsIndex/${appId}`), {
      appId: appId,
      name: appData.name,
      emoji: appData.emoji,
      authorId: currentUser.uid,
      authorHandle: userHandle,
      publishedAt,
      description: appData.description || '',
      format: appData.format || 'jsx-v1',
      sizeBytes: (appData.code || '').length + (appData.css || '').length
    });

    // Update user's app to mark as published
    await update(appRef, { isPublished: true });
    
    // Update local state
    setUserApps(prev => prev.map(a => 
      a.id === firebaseId ? { ...a, isPublished: true, code: appData.code, css: appData.css } : a
    ));
    
    // Update or add to published apps
    setPublishedApps(prev => {
      const filtered = prev.filter(p => p.appId !== appId);
      return [{
        id: appId,
        appId: appId,
        name: appData.name,
        emoji: appData.emoji,
        code: appData.code,
        css: appData.css,
        authorId: currentUser.uid,
        authorHandle: userHandle,
        publishedAt: Date.now(),
        description: appData.description || ''
      }, ...filtered];
    });
  };

  // Unpublish an app from the store
  const unpublishApp = async (firebaseId: string): Promise<void> => {
    if (!currentUser) throw new Error('Must be logged in');
    
    const app = userApps.find(a => a.id === firebaseId);
    if (!app) throw new Error('App not found');
    
    // Remove from publishedApps + index using the appId slug
    const publishedRef = ref(db, `publishedApps/${app.appId}`);
    await set(publishedRef, null);
    await set(ref(db, `publishedAppsIndex/${app.appId}`), null);

    // Update user's app to mark as unpublished
    const userAppRef = ref(db, `userApps/${currentUser.uid}/${firebaseId}`);
    await update(userAppRef, { isPublished: false });
    
    // Update local state
    setUserApps(prev => prev.map(a => 
      a.id === firebaseId ? { ...a, isPublished: false } : a
    ));
    
    setPublishedApps(prev => prev.filter(a => a.appId !== app.appId));
  };

  // Install a published app (add to user's installed list)
  const installApp = async (publishedAppId: string): Promise<void> => {
    if (!currentUser) throw new Error('Must be logged in');
    
    // Add to user's installed apps
    const installedRef = ref(db, `installedApps/${currentUser.uid}/${publishedAppId}`);
    await set(installedRef, {
      installedAt: Date.now()
    });
    
    // Update local state
    setInstalledAppIds(prev => [...prev, publishedAppId]);
  };

  // Create the orbit SDK object
  const createOrbitSDK = () => ({
    music: {
      play: () => {
        if (audioRef.current && !isPlaying) {
          audioRef.current.play();
          setIsPlaying(true);
          setHasPlayedOnce(true);
        }
      },
      pause: () => {
        if (audioRef.current && isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      },
      toggle: () => togglePlayPause(),
      next: () => nextTrack(),
      prev: () => prevTrack(),
      getCurrentTrack: () => ({
        name: tracks[currentTrackIndex].name,
        index: currentTrackIndex
      }),
      isPlaying: () => isPlaying,
      playTrack: (index: number) => playTrack(index)
    },
    apps: {
      openMusicPlayer: () => openMusicPlayer(),
      openPhotoBooth: () => openPhotoBooth(),
      openGarden: () => openGarden(),
      openGuestbook: () => openGuestbook(),
      openOrbitTV: () => openOrbitTV(),
      openGenius: () => openGenius(),
      openFileBrowser: (folder?: string) => openFileBrowser(folder || 'File Browser')
    },
    user: {
      handle: userHandle || null,
      isLoggedIn: !!currentUser
    },
    notify: (message: string) => {
      // Simple alert for now - could be enhanced to a Mac-style notification
      alert(message);
    },
    storage: {
      get: (key: string) => {
        try {
          const item = localStorage.getItem(`orbit_sdk_${key}`);
          return item ? JSON.parse(item) : null;
        } catch { return null; }
      },
      set: (key: string, value: any) => {
        try {
          localStorage.setItem(`orbit_sdk_${key}`, JSON.stringify(value));
        } catch (e) { console.error('Storage error:', e); }
      },
      clear: () => {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('orbit_sdk_')) {
            localStorage.removeItem(key);
          }
        });
      }
    }
  });

  // Route mapping for music tracks
  const musicRoutes: { [key: string]: { trackIndex: number; folderName: string } } = {
    '/lapaix': { trackIndex: 0, folderName: '01 - la paix' },
    '/yourorbit': { trackIndex: 1, folderName: '02 - your orbit' },
    '/thingschange': { trackIndex: 2, folderName: '03 - things change' },
    '/everytime': { trackIndex: 3, folderName: '04 - everytime' },
    '/needya': { trackIndex: 4, folderName: '05 - need ya' },
    '/simmering': { trackIndex: 5, folderName: '06- simmering' }
  };

  // App routes
  const appRoutes: { [key: string]: () => void } = {
    '/tv': () => openOrbitTV(),
    '/photobooth': () => openPhotoBooth(),
    '/garden': () => openGarden(),
    '/guestbook': () => openGuestbook(),
    '/orbitrun': () => openOrbitRun(),
    '/genius': () => openGenius()
  };

  // Handle initial route after component mounts
  useEffect(() => {
    if (!initialRoute || initialRoute === '/') return;

    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      // Check if it's a music route
      const musicRoute = musicRoutes[initialRoute];
      if (musicRoute) {
        // Open the specific song folder directly
        openFileBrowser(musicRoute.folderName);

        // Then open music player on top with the correct track
        setTimeout(() => {
          setCurrentTrackIndex(musicRoute.trackIndex);
          openMusicPlayer();
        }, 300);
        return;
      }

      // Check if it's an app route
      const appRoute = appRoutes[initialRoute];
      if (appRoute) {
        appRoute();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [initialRoute]);

  // Music control functions
  const fadeAudio = (targetVolume: number, duration: number = 200): Promise<void> => {
    const audio = audioRef.current;
    if (!audio) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const startVolume = audio.volume;
      const startTime = Date.now();

      const fade = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Use exponential easing for natural sound perception
        // Fading in: exponential ease-out (fast start, slow end)
        // Fading out: exponential ease-in (slow start, fast end)
        let easedProgress;
        if (targetVolume > startVolume) {
          // Fade in: ease-out exponential
          easedProgress = 1 - Math.pow(1 - progress, 3);
        } else {
          // Fade out: ease-in exponential
          easedProgress = Math.pow(progress, 3);
        }

        audio.volume = startVolume + (targetVolume - startVolume) * easedProgress;

        if (progress < 1) {
          requestAnimationFrame(fade);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(fade);
    });
  };

  const togglePlayPause = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      await fadeAudio(0);
      audioRef.current.pause();
    } else {
      audioRef.current.volume = 0;
      audioRef.current.play();
      setHasPlayedOnce(true);
      await fadeAudio(1);
    }
    setIsPlaying(!isPlaying);
  };

  const nextTrack = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
  };

  const prevTrack = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + tracks.length) % tracks.length);
  };

  const playTrack = (index: number) => {
    setCurrentTrackIndex(index);
    setIsPlaying(true);
    setHasPlayedOnce(true);

    // Show tooltip on play (for testing - always show)
    // if (!hasDismissedMusicPlayerTooltip) {
      setShowMusicPlayerTooltip(true);
    // }

    setTimeout(() => {
      audioRef.current?.play();
    }, 100);
  };

  React.useEffect(() => {
    // Trigger paint-in animation on mount
    const timer = setTimeout(() => {
      setPaintInComplete(true);
    }, 800); // Total animation time
    return () => clearTimeout(timer);
  }, []);

  // Auto-open OrbitOS launcher 1 second after desktop loads (except for music routes)
  React.useEffect(() => {
    // Don't open launcher on music routes (they have custom behavior)
    const isMusicRoute = initialRoute && musicRoutes[initialRoute];

    if (!isMusicRoute) {
      const timer = setTimeout(() => {
        openOrbitOS(undefined, undefined, true); // Use fixed position for startup
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [initialRoute]);

  // Handle track changes
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const wasPlaying = isPlaying;

    // Load the new track
    audio.load();

    // If music was playing, continue playing the new track with fade in
    if (wasPlaying) {
      audio.volume = 0;
      audio.play().then(() => {
        fadeAudio(1);
      }).catch(error => {
        console.log('Playback error:', error);
      });
    }
  }, [currentTrackIndex]);

  // Helper functions for responsive window sizing and positioning
  const getResponsiveWindowSize = (defaultWidth: number, defaultHeight: number) => {
    if (screenDimensions.isMobile) {
      // On mobile, limit to 90% of screen width
      const maxWidth = screenDimensions.width * 0.9;
      const width = Math.min(defaultWidth, maxWidth);
      // Maintain aspect ratio
      const aspectRatio = defaultHeight / defaultWidth;
      const height = width * aspectRatio;
      return { width, height };
    }
    return { width: defaultWidth, height: defaultHeight };
  };

  const getResponsiveWindowPosition = (windowCount: number) => {
    if (screenDimensions.isMobile) {
      // On mobile, center windows with more noticeable offset
      const offset = Math.min(40, windowCount * 15); // Max 40px offset, 15px per window
      const baseX = (screenDimensions.width * 0.05) + offset; // Start at 5% from left
      const baseY = 40 + offset; // Start lower to avoid menu bar
      return { x: baseX, y: baseY };
    } else {
      // Desktop positioning (existing behavior)
      const baseX = 80;
      const baseY = 60;
      const offset = 40;
      return { x: baseX + (windowCount * offset), y: baseY + (windowCount * offset) };
    }
  };

  const openMusicPlayerWithTrack = (trackIndex: number) => {
    // Play the track using global state
    playTrack(trackIndex);

    // Check if music player is already open
    const existingMusicPlayer = openWindows.find(w => w.title === 'in orbit');

    if (existingMusicPlayer) {
      // Move window to end of array (brings it to front) and focus it
      setOpenWindows(prevWindows => {
        const filtered = prevWindows.filter(w => w.id !== existingMusicPlayer.id);
        return [...filtered, existingMusicPlayer];
      });
      setFocusedWindowId(existingMusicPlayer.id);
    } else {
      // Open new music player
      openMusicPlayer();
    }
  };

  const openFileBrowser = (folderName: string = 'File Browser', startX?: number, startY?: number, parentWindowId?: string) => {
    console.log('=== openFileBrowser ===');
    console.log('folderName:', folderName);
    console.log('parentWindowId:', parentWindowId);

    // Get responsive window size
    const defaultWidth = folderName === 'In Orbit' ? 500 : 450;
    const defaultHeight = folderName === 'In Orbit' ? 400 : 300;

    let windowSize = getResponsiveWindowSize(defaultWidth, defaultHeight);

    // On mobile, make file browsers taller (use more vertical space)
    if (screenDimensions.isMobile) {
      const mobileHeight = screenDimensions.height * 0.7; // 70% of screen height
      windowSize = { ...windowSize, height: mobileHeight };
    }

    // Calculate position once and use for both animation and window
    const calculateWindowPosition = (currentWindows: OpenWindow[], context: string) => {
      console.log(`[${context}] === calculateWindowPosition ===`);
      console.log(`[${context}] currentWindows:`, currentWindows.map(w => ({id: w.id, title: w.title, pos: w.position})));
      console.log(`[${context}] looking for parentWindowId:`, parentWindowId);

      let windowX: number, windowY: number;

      if (parentWindowId) {
        const parentWindow = currentWindows.find(w => w.id === parentWindowId);
        console.log(`[${context}] Found parent window:`, parentWindow ? {title: parentWindow.title, position: parentWindow.position} : 'NOT FOUND');

        if (parentWindow) {
          const offsetX = screenDimensions.isMobile ? 50 : 60;
          const offsetY = screenDimensions.isMobile ? 50 : 60;
          windowX = parentWindow.position.x + offsetX;
          windowY = parentWindow.position.y + offsetY;

          console.log(`[${context}] Before bounds check - windowX: ${windowX}, windowY: ${windowY}`);

          // Keep within screen bounds using dynamic screen dimensions
          const maxX = screenDimensions.width - windowSize.width - 20;
          const maxY = screenDimensions.height - windowSize.height - 50;

          if (windowX > maxX) windowX = maxX;
          if (windowY > maxY) windowY = maxY;
          if (windowX < 10) windowX = 10;
          if (windowY < 40) windowY = 40;

          console.log(`[${context}] After bounds check - windowX: ${windowX}, windowY: ${windowY}`);
          console.log(`[${context}] Parent at:`, parentWindow.position, 'New window at:', windowX, windowY);
        } else {
          console.log(`[${context}] Using fallback positioning - parent not found`);
          const fallbackPos = getResponsiveWindowPosition(currentWindows.length);
          windowX = fallbackPos.x;
          windowY = fallbackPos.y;
        }
      } else {
        console.log(`[${context}] Using default positioning - no parent specified`);
        const defaultPos = getResponsiveWindowPosition(currentWindows.length);
        windowX = defaultPos.x;
        windowY = defaultPos.y;
      }

      console.log(`[${context}] Final calculated position:`, windowX, windowY);
      return { windowX, windowY };
    };
    
    const createWindow = () => {
      const windowId = `file-browser-${Date.now()}`;
      
      setOpenWindows(prevWindows => {
        const { windowX, windowY } = calculateWindowPosition(prevWindows, 'WINDOW_CREATION');
        
        const newWindow: OpenWindow = {
          id: windowId,
          title: folderName,
          component: <FileBrowser
            initialPath={folderName}
            windowId={windowId}
            onOpenFolder={(name, x, y, parentId) => openFileBrowser(name, x, y, parentId)}
            onOpenFile={(name, filePath, x, y) => openTextFile(name, filePath, x, y)}
            onLaunchApp={(name, x, y) => launchApplication(name, x, y)}
            onOpenPreview={(fileName, filePath, fileType, x, y) => openPreviewFile(fileName, filePath, fileType, x, y)}
            onPlaySong={(trackIndex) => openMusicPlayerWithTrack(trackIndex)}
          />,
          position: { x: windowX, y: windowY },
          dimensions: { width: windowSize.width, height: windowSize.height },
          resizable: true
        };
        
        console.log('🪟 FINAL WINDOW CREATED at:', windowX, windowY);
        return [...prevWindows, newWindow];
      });
      
      setFocusedWindowId(windowId);
    };
    
    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      console.log('🎬 CALCULATING ANIMATION POSITION');
      
      // We need to simulate what the state will be when createWindow runs
      // For animation, we need to predict where the window will be placed
      setOpenWindows(currentWindows => {
        // Calculate position using the current windows state that createWindow will see
        const { windowX, windowY } = calculateWindowPosition(currentWindows, 'ANIMATION_PREDICTION');

        // Use responsive window size
        const animationWidth = windowSize.width;
        const animationHeight = windowSize.height;
        const animationEndX = windowX + (animationWidth / 2);
        const animationEndY = windowY + (animationHeight / 2);

        console.log('🎯 ANIMATION will end at center:', animationEndX, animationEndY);
        console.log('🎯 ANIMATION window top-left would be:', windowX, windowY);

        // Start animation, then create window when complete
        setZoomAnimation({
          active: true,
          startX,
          startY,
          endX: animationEndX,
          endY: animationEndY,
          endWidth: animationWidth,
          endHeight: animationHeight,
          onComplete: () => {
            console.log('🎬 ANIMATION COMPLETE - now creating window');
            setZoomAnimation(null);
            createWindow();
          }
        });
        
        // Return unchanged state for now - window creation happens after animation
        return currentWindows;
      });
    } else {
      createWindow();
    }
  };

  const launchApplication = (appName: string, startX?: number, startY?: number) => {
    switch (appName) {
      case 'Music Player':
        openMusicPlayer(startX, startY);
        break;
      case 'MacPaint':
        openMacPaint(startX, startY);
        break;
      case 'Photo Booth':
        openPhotoBooth(startX, startY);
        break;
      case 'Garden':
        openGarden(startX, startY);
        break;
      case 'orbit.tv':
        openOrbitTV(startX, startY);
        break;
      case 'guestBook':
        openGuestbook(startX, startY);
        break;
      case 'adhiv.com':
        openAdhivWebsite(startX, startY);
        break;
      case 'orbitRun':
        openOrbitRun(startX, startY);
        break;
      case 'Genius':
        openGenius(startX, startY);
        break;
      case 'Text Editor':
        // For now, just open a blank text file
        openTextFile('Untitled.txt', '', startX, startY);
        break;
      case 'Terminal':
        openTerminal(startX, startY);
        break;
      default:
        console.log(`Unknown application: ${appName}`);
    }
  };

  const openTextFile = (fileName: string, filePath: string, startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(400, 300);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const createWindow = async () => {
      // Fetch the text content if filePath is provided
      let content = '';
      if (filePath) {
        try {
          const response = await fetch(filePath);
          content = await response.text();
        } catch (error) {
          console.error('Error loading text file:', error);
        }
      }

      const WINDOW_HEADER_HEIGHT = 32;

      const newWindow: OpenWindow = {
        id: `text-editor-${Date.now()}`,
        title: fileName,
        component: <TextEditor fileName={fileName} initialContent={content} readOnly={true} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight + WINDOW_HEADER_HEIGHT },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openPreviewFile = (fileName: string, filePath: string, fileType: 'image' | 'mp3' | 'video', startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const windowX = position.x;
    const windowY = position.y;

    if (fileType === 'image') {
      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        // Use responsive screen dimensions instead of hardcoded Mac screen size
        const MENU_BAR_HEIGHT = 30;
        const WINDOW_HEADER_HEIGHT = 32;
        // On mobile, use 90% width/height for larger image preview
        const widthPercentage = screenDimensions.isMobile ? 0.9 : 0.7;
        const heightPercentage = screenDimensions.isMobile ? 0.9 : 0.7;
        const maxWidth = screenDimensions.width * widthPercentage;
        const maxHeight = (screenDimensions.height - MENU_BAR_HEIGHT) * heightPercentage;
        let imgWidth = img.naturalWidth;
        let imgHeight = img.naturalHeight;

        // Scale down if too large, maintaining exact aspect ratio
        if (imgWidth > maxWidth || imgHeight > maxHeight) {
          const widthRatio = maxWidth / imgWidth;
          const heightRatio = maxHeight / imgHeight;
          const scale = Math.min(widthRatio, heightRatio);

          // Use the limiting dimension and calculate the other to preserve exact aspect ratio
          if (widthRatio < heightRatio) {
            // Width is the limiting factor
            imgWidth = Math.floor(maxWidth);
            imgHeight = Math.floor(maxWidth / (img.naturalWidth / img.naturalHeight));
          } else {
            // Height is the limiting factor
            imgHeight = Math.floor(maxHeight);
            imgWidth = Math.floor(maxHeight * (img.naturalWidth / img.naturalHeight));
          }
        }

        const createWindow = () => {
          const newWindow: OpenWindow = {
            id: `preview-${Date.now()}`,
            title: fileName,
            component: <ImagePreview fileName={fileName} filePath={filePath} />,
            position: { x: windowX, y: windowY },
            // Add header height to window dimensions so content area matches image dimensions
            dimensions: { width: imgWidth, height: imgHeight + WINDOW_HEADER_HEIGHT },
            resizable: true
          };
          setOpenWindows(prevWindows => [...prevWindows, newWindow]);
          setFocusedWindowId(newWindow.id);
        };

        // If we have start coordinates, show zoom animation
        if (startX !== undefined && startY !== undefined) {
          setZoomAnimation({
            active: true,
            startX,
            startY,
            endX: windowX + imgWidth / 2,
            endY: windowY + imgHeight / 2,
            endWidth: imgWidth,
            endHeight: imgHeight,
            onComplete: () => {
              setZoomAnimation(null);
              createWindow();
            }
          });
        } else {
          createWindow();
        }
      };
      img.src = filePath;
    } else {
      // For MP3 and video files, use responsive sizing
      let defaultWidth = 500;
      let defaultHeight = 400;

      if (fileType === 'mp3') {
        defaultWidth = 384;
        defaultHeight = 256;
      }

      const size = getResponsiveWindowSize(defaultWidth, defaultHeight);
      const windowWidth = size.width;
      const windowHeight = size.height;

      const createWindow = () => {
        let component: React.ReactNode;

        if (fileType === 'mp3') {
          component = <MP3Preview fileName={fileName} filePath={filePath} />;
        } else if (fileType === 'video') {
          component = <VideoPreview fileName={fileName} filePath={filePath} />;
        }

        const newWindow: OpenWindow = {
          id: `preview-${Date.now()}`,
          title: fileName,
          component,
          position: { x: windowX, y: windowY },
          dimensions: { width: windowWidth, height: windowHeight }
        };
        setOpenWindows(prevWindows => [...prevWindows, newWindow]);
        setFocusedWindowId(newWindow.id);
      };

      // If we have start coordinates, show zoom animation
      if (startX !== undefined && startY !== undefined) {
        setZoomAnimation({
          active: true,
          startX,
          startY,
          endX: windowX + windowWidth / 2,
          endY: windowY + windowHeight / 2,
          endWidth: windowWidth,
          endHeight: windowHeight,
          onComplete: () => {
            setZoomAnimation(null);
            createWindow();
          }
        });
      } else {
        createWindow();
      }
    }
  };

  const openMusicPlayer = (startX?: number, startY?: number, initialTrackIndex?: number, autoPlay?: boolean) => {
    // Check if music player is already open
    const existingMusicPlayer = openWindows.find(w => w.title === 'in orbit');
    if (existingMusicPlayer) {
      // Just focus the existing window instead of opening a new one
      setFocusedWindowId(existingMusicPlayer.id);
      return;
    }

    // Music player uses custom centering logic
    const size = getResponsiveWindowSize(354, 394);
    const windowWidth = size.width;
    const windowHeight = size.height;

    // Center the music player on screen
    const windowX = Math.max(10, (screenDimensions.width - windowWidth) / 2);
    const windowY = Math.max(10, (screenDimensions.height - windowHeight) / 2);

    const createWindow = () => {
      const windowId = `music-player-${Date.now()}`;
      const newWindow: OpenWindow = {
        id: windowId,
        title: 'in orbit',
        type: 'music-player',
        component: null, // Will be rendered dynamically
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight }
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus happens after window is added to DOM
      setTimeout(() => setFocusedWindowId(newWindow.id), 0);
    };
    
    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openMacPaint = (startX?: number, startY?: number) => {
    const baseX = 100;
    const baseY = 80;
    const offset = 40;
    const windowX = baseX + (openWindows.length * offset);
    const windowY = baseY + (openWindows.length * offset);
    const windowWidth = 580;
    const windowHeight = 420;
    
    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `macpaint-${Date.now()}`,
        title: 'MacPaint',
        component: <MacPaint />,
        position: { x: windowX, y: windowY }
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };
    
    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openPhotoBooth = (startX?: number, startY?: number, asModal?: boolean) => {
    const position = getResponsiveWindowPosition(openWindows.length);

    // On mobile, make window full width and adjust height for 4:3 camera + menu
    const defaultWidth = 480;
    const defaultHeight = 460;

    let windowWidth: number;
    let windowHeight: number;

    if (screenDimensions.isMobile) {
      const maxWidth = screenDimensions.width * 0.9;
      windowWidth = Math.min(defaultWidth, maxWidth);
      // Height = (width * 3/4 for 4:3 camera) + 100px for menu + 32px for window header
      const cameraHeight = (windowWidth * 3) / 4;
      windowHeight = cameraHeight + 100 + 32;
    } else {
      windowWidth = defaultWidth;
      windowHeight = defaultHeight;
    }

    const windowX = position.x;
    const windowY = position.y;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `photobooth-${Date.now()}`,
        title: 'Photo Booth',
        component: <PhotoBooth onOpenPreview={(photoUrl, photoId) => openPreviewFile(photoId, photoUrl, 'image')} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        modal: asModal
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus happens after window is added to DOM
      setTimeout(() => setFocusedWindowId(newWindow.id), 0);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openGarden = (startX?: number, startY?: number) => {
    // Check if Garden window already exists
    const existingGarden = openWindows.find(w => w.title === 'Garden');
    if (existingGarden) {
      // Just focus the existing window
      setFocusedWindowId(existingGarden.id);
      return;
    }

    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(480, 360);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    let windowHeight = size.height;

    // On mobile, add extra height to account for the toolbar (50px) + canvas margin (10px) + padding (8px)
    // This ensures the canvas content area is 4:3, not the entire window
    if (screenDimensions.isMobile) {
      windowHeight = size.height + 68; // 50px toolbar + 10px margin + 8px padding
    }

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `garden-${Date.now()}`,
        title: 'Garden',
        component: <Garden onOpenLoginSignup={() => openLoginSignup()} onOpenPhotoBooth={() => openPhotoBooth()} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus happens after window is added to DOM
      setTimeout(() => setFocusedWindowId(newWindow.id), 0);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openLoginSignup = (startX?: number, startY?: number) => {
    // Check if Login/Signup window already exists
    const existingLogin = openWindows.find(w => w.title === 'Login / Signup');
    if (existingLogin) {
      // Just focus the existing window
      setFocusedWindowId(existingLogin.id);
      return;
    }

    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(400, 380);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const windowId = `login-${Date.now()}`;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: windowId,
        title: 'Login / Signup',
        component: <LoginSignupWindow onClose={() => closeWindow(windowId)} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        modal: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openOrbitTV = (startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(400, 300); // 4:3 aspect ratio

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `orbit-tv-${Date.now()}`,
        title: 'orbit.tv',
        component: <OrbitTV />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight }
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openImageUrl = (imageUrl: string, title: string) => {
    const position = getResponsiveWindowPosition(openWindows.length);

    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      const MENU_BAR_HEIGHT = 30;
      const WINDOW_HEADER_HEIGHT = 32;
      // Use responsive screen dimensions instead of hardcoded values
      // On mobile, use 90% of screen for larger preview
      const maxWidth = screenDimensions.width * (screenDimensions.isMobile ? 0.9 : 0.7);
      const maxHeight = (screenDimensions.height - MENU_BAR_HEIGHT) * (screenDimensions.isMobile ? 0.9 : 0.7);
      let imgWidth = img.naturalWidth;
      let imgHeight = img.naturalHeight;

      // Scale down if too large
      if (imgWidth > maxWidth || imgHeight > maxHeight) {
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const scale = Math.min(widthRatio, heightRatio);

        if (widthRatio < heightRatio) {
          imgWidth = Math.floor(maxWidth);
          imgHeight = Math.floor(maxWidth / (img.naturalWidth / img.naturalHeight));
        } else {
          imgHeight = Math.floor(maxHeight);
          imgWidth = Math.floor(maxHeight * (img.naturalWidth / img.naturalHeight));
        }
      }

      const windowId = `preview-${Date.now()}`;
      const newWindow: OpenWindow = {
        id: windowId,
        title: title,
        component: <ImagePreview fileName={title} filePath={imageUrl} />,
        position: { x: position.x, y: position.y },
        dimensions: { width: imgWidth, height: imgHeight + WINDOW_HEADER_HEIGHT },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus is set after window is added
      setTimeout(() => {
        setFocusedWindowId(windowId);
      }, 0);
    };
    img.src = imageUrl;
  };

  const openGardenUrl = (gardenPageId: string, userHandle: string) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(400, 300); // 4:3 ratio for garden

    const windowX = position.x;
    const windowY = position.y;
    const gardenWidth = size.width;
    const gardenHeight = size.height;

    const gardenUrl = `https://mindgarden.page/${userHandle}/${gardenPageId}`;

    const windowId = `garden-preview-${Date.now()}`;
    const newWindow: OpenWindow = {
      id: windowId,
      title: `@${userHandle}'s garden`,
      component: (
        <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <iframe
            src={gardenUrl}
            title={`@${userHandle}'s garden`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        </div>
      ),
      position: { x: windowX, y: windowY },
      dimensions: { width: gardenWidth, height: gardenHeight },
      resizable: true
    };

    // Add window and immediately focus it to ensure it's brought to front
    setOpenWindows(prevWindows => [...prevWindows, newWindow]);
    // Use setTimeout to ensure focus is set after window is added to DOM
    setTimeout(() => {
      setFocusedWindowId(windowId);
    }, 0);
  };

  const openGuestbook = (startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);

    // On mobile, make guestbook take up 80% of screen height
    let windowWidth: number;
    let windowHeight: number;

    if (screenDimensions.isMobile) {
      const maxWidth = screenDimensions.width * 0.9;
      windowWidth = Math.min(500, maxWidth);
      windowHeight = screenDimensions.height * 0.8; // 80% of screen height on mobile
    } else {
      windowWidth = 500;
      windowHeight = 490;
    }

    const windowX = position.x;
    const windowY = position.y;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `guestbook-${Date.now()}`,
        title: 'guestBook',
        component: <Guestbook onOpenImage={openImageUrl} onOpenGarden={openGardenUrl} onOpenLoginSignup={() => openLoginSignup()} onOpenPhotoBooth={() => openPhotoBooth(undefined, undefined, true)} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus happens after window is added to DOM
      setTimeout(() => setFocusedWindowId(newWindow.id), 0);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openOrbitOS = (startX?: number, startY?: number, useFixedPosition?: boolean) => {
    const size = getResponsiveWindowSize(400, 400); // Square dimensions for 2x2 grid

    let windowX: number;
    let windowY: number;

    // Use fixed position if no windows are open OR if explicitly requested (startup)
    if (openWindows.length === 0 || useFixedPosition) {
      // Fixed position for startup or empty desktop
      if (screenDimensions.isMobile) {
        // Center the window on mobile
        windowX = (screenDimensions.width - size.width) / 2;
        windowY = 40; // Below menu bar
      } else {
        // Desktop fixed position
        windowX = 340;
        windowY = 20;
      }
    } else {
      // Dynamic cascading position for manual opens with existing windows
      const position = getResponsiveWindowPosition(openWindows.length);
      windowX = position.x;
      windowY = position.y;
    }

    const windowWidth = size.width;
    const windowHeight = size.height;

    // Helper functions to simulate desktop icon double-clicks
    const simulateIconDoubleClick = (iconName: string) => {
      const icon = desktopIcons.find(i => i.name === iconName);
      if (icon) {
        const iconCenterX = icon.position.x + 40; // Icon width/2
        const iconCenterY = icon.position.y + 60 + 30; // Icon height + label + MenuBar height
        icon.action(iconCenterX, iconCenterY);
      }
    };

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `orbitos-${Date.now()}`,
        title: 'orbitOS',
        component: (
          <OrbitOS
            onOpenMusicPlayer={() => simulateIconDoubleClick('Music Player')}
            onOpenPhotoBooth={() => simulateIconDoubleClick('Photo Booth')}
            onOpenGuestbook={() => simulateIconDoubleClick('guestBook')}
            onOpenOrbitRun={() => simulateIconDoubleClick('orbitRun')}
          />
        ),
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: false
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // Always show zoom animation from top left area (just below menu bar)
    const animStartX = startX ?? 100;
    const animStartY = startY ?? 50;

    setZoomAnimation({
      active: true,
      startX: animStartX,
      startY: animStartY,
      endX: windowX + windowWidth / 2,
      endY: windowY + windowHeight / 2,
      endWidth: windowWidth,
      endHeight: windowHeight,
      onComplete: () => {
        setZoomAnimation(null);
        createWindow();
      }
    });
  };

  const openAdhivWebsite = (startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(500, 380);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `adhiv-website-${Date.now()}`,
        title: 'adhiv.com',
        component: <AdhivWebsite />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight }
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openGenius = (startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);

    // Similar to Guestbook: on mobile take up 80% of screen height
    let windowWidth: number;
    let windowHeight: number;

    if (screenDimensions.isMobile) {
      const maxWidth = screenDimensions.width * 0.9;
      windowWidth = Math.min(500, maxWidth);
      windowHeight = screenDimensions.height * 0.8; // 80% of screen height on mobile
    } else {
      // Desktop: 60% of screen width, maintaining 7:5 aspect ratio
      windowWidth = screenDimensions.width * 0.6;
      windowHeight = windowWidth * (5 / 7); // Maintain aspect ratio
    }

    const windowX = position.x;
    const windowY = position.y;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `genius-${Date.now()}`,
        title: 'Genius',
        component: null, // Will be rendered dynamically
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: true,
        type: 'genius' // Mark as special type to handle dynamic rendering
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setFocusedWindowId(newWindow.id);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openOrbitRun = (startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    // On mobile, make the window taller (600x550 instead of 600x380)
    const baseHeight = screenDimensions.isMobile ? 550 : 380;
    const size = getResponsiveWindowSize(600, baseHeight);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const createWindow = () => {
      const newWindow: OpenWindow = {
        id: `orbitrun-${Date.now()}`,
        title: 'orbitRun',
        component: <OrbitRun onOpenLoginSignup={() => openLoginSignup()} />,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight }
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      // Use setTimeout to ensure focus happens after window is added to DOM
      setTimeout(() => setFocusedWindowId(newWindow.id), 0);
    };

    // If we have start coordinates, show zoom animation
    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openTerminal = (startX?: number, startY?: number) => {
    // Check if Terminal is already open
    const existingTerminal = openWindows.find(w => w.title === 'Terminal');
    if (existingTerminal) {
      setFocusedWindowId(existingTerminal.id);
      return;
    }

    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(640, 500); // Room for the app list + chat

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const createWindow = () => {
      const windowId = `terminal-${Date.now()}`;
      const newWindow: OpenWindow = {
        id: windowId,
        title: 'Terminal',
        type: 'terminal',
        component: null, // Rendered dynamically
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setTimeout(() => setFocusedWindowId(windowId), 0);
    };

    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const openTerminalPreview = (code: string, css: string, appName: string, appEmoji: string) => {
    // One preview window per app: re-render in place instead of stacking windows
    const windowId = `preview-${appName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
    const component = (
      <TerminalPreview
        key={Date.now()}
        code={code}
        css={css}
        appName={appName}
        appEmoji={appEmoji}
        orbitSDK={createOrbitSDK()}
        onError={(error) => console.error('Preview error:', error)}
      />
    );

    // Functional update so rapid successive calls (agentic edits) see fresh state
    setOpenWindows(prevWindows => {
      if (prevWindows.some(w => w.id === windowId)) {
        return prevWindows.map(w =>
          w.id === windowId ? { ...w, title: `${appEmoji} ${appName}`, component } : w
        );
      }
      const position = getResponsiveWindowPosition(prevWindows.length);
      const size = getResponsiveWindowSize(400, 400);
      return [...prevWindows, {
        id: windowId,
        title: `${appEmoji} ${appName}`,
        component,
        position: { x: position.x, y: position.y },
        dimensions: { width: size.width, height: size.height },
        resizable: true
      }];
    });
    setTimeout(() => setFocusedWindowId(windowId), 0);
  };

  const openCodeEditorWindow = (code: string, css: string, appName: string, onSave: (code: string, css: string) => void) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(600, 500);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    const windowId = `code-editor-${Date.now()}`;
    const newWindow: OpenWindow = {
      id: windowId,
      title: `Edit: ${appName}`,
      component: (
        <CodeEditor
          initialCode={code}
          initialCss={css}
          appName={appName}
          onSave={(newCode, newCss) => {
            onSave(newCode, newCss);
          }}
          onClose={() => closeWindow(windowId)}
        />
      ),
      position: { x: windowX, y: windowY },
      dimensions: { width: windowWidth, height: windowHeight },
      resizable: true
    };
    setOpenWindows(prevWindows => [...prevWindows, newWindow]);
    setTimeout(() => setFocusedWindowId(windowId), 0);
  };

  const openUserApp = (app: UserApp, startX?: number, startY?: number) => {
    const position = getResponsiveWindowPosition(openWindows.length);
    const size = getResponsiveWindowSize(400, 400);

    const windowX = position.x;
    const windowY = position.y;
    const windowWidth = size.width;
    const windowHeight = size.height;

    // Payloads may be index-only (installed apps, html-v2 apps): fetch on open
    const createWindow = async () => {
      let component: React.ReactNode;

      if (app.format === 'html-v2') {
        const full = await fetchPublishedApp(app.appId);
        if (!full || !full.html) {
          console.error('html app payload not found:', app.appId);
          return;
        }
        component = (
          <HtmlAppFrame
            html={full.html}
            appId={app.appId}
            appName={app.name}
            appEmoji={app.emoji}
            orbitSDK={createOrbitSDK()}
          />
        );
      } else {
        let code = app.code;
        let css = app.css;
        if (!code) {
          const full = await fetchPublishedApp(app.appId);
          if (!full) {
            console.error('app payload not found:', app.appId);
            return;
          }
          code = full.code || '';
          css = full.css || '';
        }
        component = (
          <TerminalPreview
            code={code}
            css={css}
            appName={app.name}
            appEmoji={app.emoji}
            orbitSDK={createOrbitSDK()}
            onError={(error) => console.error('App error:', error)}
          />
        );
      }

      const windowId = `user-app-${app.id}-${Date.now()}`;
      const newWindow: OpenWindow = {
        id: windowId,
        title: `${app.emoji} ${app.name}`,
        component,
        position: { x: windowX, y: windowY },
        dimensions: { width: windowWidth, height: windowHeight },
        resizable: true
      };
      setOpenWindows(prevWindows => [...prevWindows, newWindow]);
      setTimeout(() => setFocusedWindowId(windowId), 0);
    };

    if (startX !== undefined && startY !== undefined) {
      setZoomAnimation({
        active: true,
        startX,
        startY,
        endX: windowX + windowWidth / 2,
        endY: windowY + windowHeight / 2,
        endWidth: windowWidth,
        endHeight: windowHeight,
        onComplete: () => {
          setZoomAnimation(null);
          createWindow();
        }
      });
    } else {
      createWindow();
    }
  };

  const closeWindow = (windowId: string) => {
    // Check if we're closing the music player window
    const closingWindow = openWindows.find(w => w.id === windowId);
    // For testing - always show tooltip
    // if (closingWindow?.type === 'music-player' && hasPlayedOnce && !hasDismissedMusicPlayerTooltip) {
    if (closingWindow?.type === 'music-player' && hasPlayedOnce) {
      setShowMusicPlayerTooltip(true);
    }

    setOpenWindows(openWindows.filter(window => window.id !== windowId));
    if (focusedWindowId === windowId) {
      setFocusedWindowId(null);
    }
  };

  const downloadAllPhotos = async () => {
    try {
      // Get photos from localStorage
      const saved = localStorage.getItem('photoboothPhotos');
      if (!saved) {
        alert('No photos to download');
        return;
      }

      const photos = JSON.parse(saved);
      if (!Array.isArray(photos) || photos.length === 0) {
        alert('No photos to download');
        return;
      }

      // Create a new JSZip instance
      const zip = new JSZip();

      // Add each photo to the zip
      photos.forEach((photo: any, index: number) => {
        // Extract base64 data from data URL
        const base64Data = photo.dataUrl.split(',')[1];
        // Add to zip with filename
        zip.file(`photo-${index + 1}.png`, base64Data, { base64: true });
      });

      // Generate the zip file
      const content = await zip.generateAsync({ type: 'blob' });

      // Create download link and trigger download
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `photobooth-photos-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading photos:', error);
      alert('Error downloading photos');
    }
  };

  const focusWindow = (windowId: string) => {
    setFocusedWindowId(windowId);
  };

  const desktopIcons: Array<{
    name: string;
    icon: string | any;
    position: { x: number; y: number };
    action: (x?: number, y?: number) => void;
  }> = [
    { name: 'Music Player', icon: cdIcon, position: { x: 20, y: 40 }, action: (x?: number, y?: number) => openMusicPlayer(x, y) },
    { name: 'credits.txt', icon: fileIcon, position: { x: 20, y: 160 }, action: (x?: number, y?: number) => openTextFile('credits.txt', '', x, y) },
    { name: 'in orbit', icon: folderPurple, position: { x: 130, y: 40 }, action: (x?: number, y?: number) => openFileBrowser('In Orbit', x, y) },
    { name: 'Photo Booth', icon: photoboothIcon, position: { x: 230, y: 40 }, action: (x?: number, y?: number) => openPhotoBooth(x, y) },
    { name: 'Garden', icon: gardenIcon, position: { x: 130, y: 160 }, action: (x?: number, y?: number) => openGarden(x, y) },
    { name: 'orbit.tv', icon: tvIcon, position: { x: 230, y: 160 }, action: (x?: number, y?: number) => openOrbitTV(x, y) },
    { name: 'Trash', icon: trashIcon, position: { x: 20, y: 260 }, action: (x?: number, y?: number) => openFileBrowser('Trash', x, y) },
    { name: 'guestBook', icon: guestbookIcon, position: { x: 130, y: 260 }, action: (x?: number, y?: number) => openGuestbook(x, y) },
    { name: 'adhiv.com', icon: adhivIcon, position: { x: 230, y: 260 }, action: (x?: number, y?: number) => openAdhivWebsite(x, y) },
    { name: 'orbitRun', icon: orbitRunIcon, position: { x: 20, y: 360 }, action: (x?: number, y?: number) => openOrbitRun(x, y) },
    { name: 'Genius', icon: geniusIcon, position: { x: 130, y: 360 }, action: (x?: number, y?: number) => openGenius(x, y) },
    { name: 'Terminal', icon: terminalIcon, position: { x: 230, y: 360 }, action: (x?: number, y?: number) => openTerminal(x, y) }
  ];

  // Generate user app icons dynamically - position them in the right side of the desktop
  // Combine user's own apps AND installed apps (live links to published versions)
  const ownAppIcons = userApps.map((app) => ({
    id: app.id,
    name: app.name,
    emoji: app.emoji,
    code: app.code,
    css: app.css,
    isOwn: true
  }));
  
  // Get installed apps that aren't the user's own (live links)
  const installedAppsData = installedAppIds
    .filter(id => !userApps.some(app => app.id === id)) // Exclude own apps
    .map(id => publishedApps.find(app => app.id === id))
    .filter(Boolean) as typeof publishedApps;
  
  const installedAppIcons = installedAppsData.map((app) => ({
    id: app.id,
    name: app.name,
    emoji: app.emoji,
    code: app.code,
    css: app.css,
    isOwn: false,
    authorHandle: app.authorHandle
  }));
  
  // Combine and position all app icons
  const allUserAppIcons = [...ownAppIcons, ...installedAppIcons];
  
  const userAppIcons = allUserAppIcons.map((app, index) => {
    // Position user apps on the right side, starting at x: 340
    const col = Math.floor(index / 4); // 4 rows before moving to next column
    const row = index % 4;
    return {
      name: app.name,
      icon: app.emoji,
      isEmoji: true,
      position: { x: 340 + (col * 110), y: 40 + (row * 100) },
      action: (x?: number, y?: number) => {
        // For installed apps, get the live version from publishedApps
        if (!app.isOwn) {
          const liveApp = publishedApps.find(p => p.appId === app.id);
          if (liveApp) {
            openUserApp({
              id: liveApp.id,
              appId: liveApp.appId,
              name: liveApp.name,
              emoji: liveApp.emoji,
              code: liveApp.code || '',
              css: liveApp.css || '',
              createdAt: liveApp.publishedAt,
              updatedAt: liveApp.publishedAt,
              isPublished: true,
              description: liveApp.description,
              format: liveApp.format
            }, x, y);
          }
        } else {
          const userApp = userApps.find(a => a.id === app.id);
          if (userApp) {
            openUserApp(userApp, x, y);
          }
        }
      },
      appId: app.id
    };
  });

  // Get the focused window's title for the menu bar
  const focusedWindow = openWindows.find(w => w.id === focusedWindowId);
  const focusedAppName = focusedWindow?.title || undefined;
  
  // Check if focused window is a user app (has appId in the window id)
  const focusedUserAppId = focusedWindow?.id.startsWith('user-app-') 
    ? (() => {
        const firebaseId = focusedWindow.id.replace('user-app-', '');
        const userApp = userApps.find(a => a.id === firebaseId);
        return userApp?.isPublished ? userApp.appId : null;
      })()
    : null;

  // Handle menu actions
  const handleMenuAction = (action: string, data?: any) => {
    console.log('Menu action:', action, data);

    switch (action) {
      case 'closeWindow':
        if (focusedWindowId) {
          closeWindow(focusedWindowId);
        }
        break;
      case 'openFolder':
        openFileBrowser(data);
        break;
      case 'viewAlbumArt':
        // Open cover.jpg from In Orbit folder
        const coverPath = require('../browser/in orbit/cover.jpg');
        openPreviewFile('cover.jpg', coverPath, 'image');
        break;
      case 'playPause':
        togglePlayPause();
        break;
      case 'nextTrack':
        nextTrack();
        break;
      case 'prevTrack':
        prevTrack();
        break;
      case 'playTrack':
        playTrack(data);
        break;
      case 'openOrbitOS':
        openOrbitOS();
        break;
      case 'openLoginSignup':
        openLoginSignup();
        break;
      case 'downloadAllPhotos':
        downloadAllPhotos();
        break;
      case 'openMusicPlayer':
        // Check if music player is already open
        const existingMusicPlayer = openWindows.find(w => w.title === 'in orbit');
        if (existingMusicPlayer) {
          // Focus the existing window
          setFocusedWindowId(existingMusicPlayer.id);
        } else {
          // Open new music player
          openMusicPlayer();
        }
        break;
    }
  };

  return (
    <div className={`desktop ${!paintInComplete ? 'paint-in' : ''}`}>
      <div className="desktop-background" />
      {/* Global audio element for music playback */}
      <audio
        ref={audioRef}
        src={tracks[currentTrackIndex].file}
        onEnded={nextTrack}
      />
      <MenuBar
        focusedApp={focusedAppName}
        focusedUserAppId={focusedUserAppId}
        onMenuAction={handleMenuAction}
        hasPlayedOnce={hasPlayedOnce}
        isPlaying={isPlaying}
        currentTrack={tracks[currentTrackIndex].name}
        onPlayPause={togglePlayPause}
        onNext={nextTrack}
        onPrev={prevTrack}
        showMusicPlayerTooltip={showMusicPlayerTooltip}
        onDismissMusicPlayerTooltip={() => {
          setShowMusicPlayerTooltip(false);
          setHasDismissedMusicPlayerTooltip(true);
        }}
      />
      <div className="desktop-content" onClick={() => setSelectedIconIndex(null)}>
        {desktopIcons.map((icon, index) => (
          <DesktopIcon
            key={index}
            name={icon.name}
            icon={icon.icon}
            position={icon.position}
            onDoubleClick={() => {
              // Desktop icons use absolute positioning within the desktop
              const iconCenterX = icon.position.x + 40; // Icon width/2
              const iconCenterY = icon.position.y + 60 + 30; // Icon height + label + MenuBar height
              icon.action(iconCenterX, iconCenterY);
            }}
            isSelected={selectedIconIndex === index}
            onSingleClick={() => setSelectedIconIndex(index)}
            paintInDelay={index * 50}
          />
        ))}
        {/* User app icons */}
        {userAppIcons.map((icon, index) => (
          <DesktopIcon
            key={`user-app-${icon.appId}`}
            name={icon.name}
            icon={icon.icon}
            position={icon.position}
            isEmoji={icon.isEmoji}
            onDoubleClick={() => {
              const iconCenterX = icon.position.x + 40;
              const iconCenterY = icon.position.y + 60 + 30;
              icon.action(iconCenterX, iconCenterY);
            }}
            isSelected={selectedIconIndex === desktopIcons.length + index}
            onSingleClick={() => setSelectedIconIndex(desktopIcons.length + index)}
            paintInDelay={(desktopIcons.length + index) * 50}
          />
        ))}
        
        {openWindows.map(window => {
          // Use custom dimensions if available, otherwise use title-based defaults
          const width = window.dimensions?.width ?? (
            window.title === 'MacPaint' ? 580 :
            window.title === 'in orbit' ? 390 :
            window.title === 'In Orbit' ? 500 :
            window.title === 'Photo Booth' ? 480 :
            window.title === 'Garden' ? 480 :
            window.title === 'orbit.tv' ? 400 :
            window.title === 'Guestbook' ? 420 :
            window.title === 'adhiv.com' ? 500 :
            window.title === 'Trash' ? 450 :
            // Song folders get larger windows
            (window.title.startsWith('01 - ') || window.title.startsWith('02 - ') ||
             window.title.startsWith('03 - ') || window.title.startsWith('04 - ') ||
             window.title.startsWith('05 - ') || window.title.startsWith('06- ')) ? 470 : 450
          );
          const height = window.dimensions?.height ?? (
            window.title === 'MacPaint' ? 420 :
            window.title === 'in orbit' ? 394 :
            window.title === 'In Orbit' ? 400 :
            window.title === 'Photo Booth' ? 460 :
            window.title === 'Garden' ? 360 :
            window.title === 'orbit.tv' ? 300 :
            window.title === 'Guestbook' ? 500 :
            window.title === 'adhiv.com' ? 380 :
            window.title === 'Trash' ? 320 :
            // Song folders get larger windows
            (window.title.startsWith('01 - ') || window.title.startsWith('02 - ') ||
             window.title.startsWith('03 - ') || window.title.startsWith('04 - ') ||
             window.title.startsWith('05 - ') || window.title.startsWith('06- ')) ? 360 : 300
          );

          return (
            <Window
              key={window.id}
              title={window.title}
              initialPosition={window.position}
              onClose={() => closeWindow(window.id)}
              onFocus={() => focusWindow(window.id)}
              isFocused={focusedWindowId === window.id}
              width={width}
              height={height}
              resizable={window.resizable}
              modal={window.modal}
            >
              {window.type === 'music-player' ? (
                <MusicPlayer
                  currentTrackIndex={currentTrackIndex}
                  isPlaying={isPlaying}
                  audioRef={audioRef}
                  onPlayPause={togglePlayPause}
                  onNext={nextTrack}
                  onPrev={prevTrack}
                  onTrackChange={playTrack}
                />
              ) : window.type === 'genius' ? (
                <Genius
                  currentTrackIndex={currentTrackIndex}
                  isPlaying={isPlaying}
                  onPlayTrack={playTrack}
                  onTogglePlay={togglePlayPause}
                />
              ) : window.type === 'terminal' ? (
                <Terminal
                  onSaveApp={saveUserApp}
                  onUpdateApp={updateUserApp}
                  onDeleteApp={deleteUserApp}
                  onPublishApp={publishApp}
                  onUnpublishApp={unpublishApp}
                  onInstallApp={installApp}
                  onOpenPreview={openTerminalPreview}
                  onOpenCodeEditor={openCodeEditorWindow}
                  userApps={userApps}
                  publishedApps={publishedApps}
                  installedAppIds={installedAppIds}
                  isLoggedIn={!!currentUser}
                  userHandle={userHandle}
                  onOpenLoginSignup={openLoginSignup}
                />
              ) : (
                window.component
              )}
            </Window>
          );
        })}
        
        {zoomAnimation && (
          <ZoomAnimation
            startX={zoomAnimation.startX}
            startY={zoomAnimation.startY}
            endX={zoomAnimation.endX}
            endY={zoomAnimation.endY}
            endWidth={zoomAnimation.endWidth}
            endHeight={zoomAnimation.endHeight}
            onComplete={zoomAnimation.onComplete}
          />
        )}
      </div>
    </div>
  );
};

export default Desktop;